import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";

const filterK2TimeSeriesSchema = z.object({
  k2_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterK2TimeSeriesSchema = z.infer<typeof filterK2TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterK2TimeSeriesSchema>[] = [
  {
    name: "k2_static_datum_name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_contains", key: "isin", extract: "text", bucket: "equals" },
];

const fields: Field[] = [
  {
    name: "k2_static_datum_name_contains",
    label: `Actif`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_static_data",
    id_column: "k2_static_datum_id",
    column_name: "name",
    col_span: 2,
    filters: makeFiltersBuilder<FilterK2TimeSeriesSchema>(
      assetDepFields,
      "k2_static_datum_name_contains",
    ),
  },
  {
    name: "isin_contains",
    label: `ISIN`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_static_data",
    id_column: "k2_static_datum_id",
    column_name: "isin",
    filters: makeFiltersBuilder<FilterK2TimeSeriesSchema>(
      assetDepFields,
      "isin_contains",
    ),
  },
  {
    name: "currency_contains",
    label: `Devise`,
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "date",
    type: "dateRange",
    label: "Date de valeur",
    from: { name: "date_from" },
    to: { name: "date_to" },
    col_span: 2,
  },
  {
    name: "create",
    type: "dateRange",
    label: "Date de création",
    from: { name: "create_from" },
    to: { name: "create_to" },
    col_span: 2,
  },
];

export function FilterK2TimeSeries() {
  return (
    <FilterTable
      schema={filterK2TimeSeriesSchema}
      client="k2"
      tableName="k2_time_series"
      fields={fields}
      columns={2}
    />
  );
}
